import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from "react-router-dom"
import {
    Table,
    Space,
    Card,
    Button,
    Tag,
    Drawer,
    Col,
    Row,
    Typography,
    Tooltip,
    Input,
    Select,
    Badge,
    Spin,
    Modal,
    Checkbox,
    message,
    List,
    Divider
} from 'antd'
import styled from 'styled-components'
import { Product } from '@a4b/api/src/modules/Monetisation/Puja/types'
import { SortAscendingOutlined, UploadOutlined } from '@ant-design/icons'
import { TablePaginationConfig } from 'antd/es/table'
import { CustomLoader } from '@/components/Loader'
import usePujaStore from '@/modules/monetisation/puja/stores/puja-store'
import { A4B_APPS } from '@/utils/constants'
import { useAppContext } from '@/components/AppContext'
import ProductListDrawer from '../ProductListDrawer'
import { showErrorToast } from '@/utils/helper'


const { Text } = Typography


type AddedItems = { added: Product[] }


interface Props {
    AddedItems: AddedItems
    triggerEdit: (record: any) => void
    productList: Product[]
    createItem: () => void
    added: (item: Product, type?: string) => void
    currentStep: number
    nextHandler: () => void
    previousHandler: () => void
    handleTableChange: (paginationParams: TablePaginationConfig) => void
    paginationParams: TablePaginationConfig
    loading: boolean
    sortBy?: ('price' | 'position' | 'created_at')
    updateSortBy: (sortBy?: ('price' | 'position' | 'created_at')) => void
    getStoreProductList: () => void
    resetTrigger: boolean
    setResetTrigger: (state: boolean) => void
    searchQuery: string
    onSearchChange: (value: string) => void
}


const Container = styled(Card)`
  display: flex;
  flex-direction: column;
  .ant-card-body {
    flex-grow: 1;
  }
`


const BodyContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
`


const LabelRightAlign = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding-top: 10px;
`


const StoreCard = styled(Card)`
  margin: 8px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background-color: #f0f5ff;
  }
`


const ProductCard = styled(Card)`
  margin: 8px 0;
  border-radius: 8px;
  background-color: #f9f9f9;
`


const InventoryList: React.FC<Props> = ({
    AddedItems,
    triggerEdit,
    productList,
    createItem,
    added,
    currentStep,
    nextHandler,
    previousHandler,
    paginationParams,
    handleTableChange,
    loading,
    sortBy,
    updateSortBy,
    getStoreProductList,
    resetTrigger,
    setResetTrigger,
    searchQuery,
    onSearchChange,
}) => {
    const { networkInstance, countryState, app } = useAppContext()
    const monetisationPujaApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationPujaApi : networkInstance.clientWithHeaders.monetisationAstroPujaApi
    const monetisationOfferingsApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationOfferingsApi : networkInstance.clientWithHeaders.monetisationOfferingsApi


    const navigate = useNavigate()
    const dataSource = AddedItems?.added
    const [filterData, setFilterData] = useState(dataSource)
    const [title, setTitle] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [showItemsPanel, setShowItemsPannel] = useState(false)


    const [checkedAll, setCheckedAll] = useState(false)
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>([])
    const [showUploadButton, setShowUploadButton] = useState<boolean>(false)
    const [showUpdateExpModal, setShowUpdateExpModal] = useState<boolean>(false)
    const { experiment, variant, setExperiment, setVariant } = usePujaStore()
    const [searchParams, setSearchParams] = useSearchParams()
    const store_id = searchParams.get('store_id') || ''


    // Chadhava drawer states
    const [showChadhavaDrawer, setShowChadhavaDrawer] = useState(false)
    const [storeListLoading, setStoreListLoading] = useState(false)
    const [storeList, setStoreList] = useState<any[]>([])
    const [storeListSearch, setStoreListSearch] = useState('')
    const [drawerStoreIdSearch, setDrawerStoreIdSearch] = useState('') // Search by store ID inside drawer
    const [drawerTitleSearch, setDrawerTitleSearch] = useState('') // Search by title inside drawer


    // Selected store and products
    const [selectedStore, setSelectedStore] = useState<any>(null)
    const [productListState, setProductListState] = useState<any[]>([])
    const [productListLoading, setProductListLoading] = useState(false)

    // Hub list for mixed stores
    const [hubList, setHubList] = useState<any[]>([])

    // Temple list for showing temple names
    const [templeList, setTempleList] = useState<any>()

    useEffect(() => {
        setExperiment(undefined)
        setVariant(undefined)
    }, [setExperiment, setVariant])


    useEffect(() => {
        setFilterData(dataSource)
        setTitle('')
        setDisplayName('')
    }, [dataSource])


    const filterByTitleInput = () => {
        return <Input
            placeholder="Search Title"
            size='small'
            value={title}
            onChange={e => {
                const currValue = e.target.value;
                setTitle(currValue);
            }}
        />
    }


    useEffect(() => {
        const filterByTitle = dataSource
            .filter((entry: any) => {
                return entry.title.toLowerCase().includes(title.toLowerCase())
            })
            .filter((entry: any) => {
                if (displayName === "") {
                    return true;
                }
                return entry.display_name?.toLowerCase().includes(displayName.toLowerCase())
            });
        if (title.trim() === "" && displayName.trim() === "") {
            setFilterData(dataSource);
            return
        }
        setFilterData(filterByTitle);
    }, [title, displayName, dataSource])


    const handleSelectAll = (checked: boolean) => {
        setCheckedAll(checked);
        setShowUploadButton(true)
        if (checked) {
            setSelectedRowKeys(filterData.map((item) => item.id));
        } else {
            setSelectedRowKeys([]);
        }
    };


    const handleRowSelection = (key: number, checked: boolean) => {
        setCheckedAll(false);
        setSelectedRowKeys((prev: any) =>
            checked ? [...prev, key] : prev.filter((rowKey: number) => rowKey !== key)
        );
    };


    useEffect(() => {
        if (selectedRowKeys?.length > 0) {
            setShowUploadButton(true)
        } else {
            setShowUploadButton(false)
        }
    }, [selectedRowKeys, checkedAll])


    const shouldShowCheckboxColumn = filterData.some(row => row.exp_linked?.toString() !== 'true');


    const columns: any = [
        ...(experiment && shouldShowCheckboxColumn
            ? [
                {
                    title: (
                        <Checkbox
                            indeterminate={
                                !checkedAll &&
                                selectedRowKeys.length > 0 &&
                                selectedRowKeys.length < filterData.length
                            }
                            checked={checkedAll}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                    ),
                    render: (_: any, record: any) =>
                        record?.exp_linked?.toString() !== 'true' ? (
                            <Checkbox
                                checked={checkedAll || selectedRowKeys.includes(record.id)}
                                onChange={(e) =>
                                    handleRowSelection(record.id, e?.target?.checked)
                                }
                            />
                        ) : null,
                    width: "10%",
                },
            ]
            : []),
        {
            title: 'Inventory Id',
            dataIndex: 'id',
            key: 'inventory_id',
            width: '25%',
            render: (store_id: string, record: any) => (
                <div style={{ display: 'grid', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                        {record?.sub_type && (
                            <Tag color='blue' style={{ width: '75px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record?.sub_type}
                            </Tag>
                        )}
                        {record?.origin_hub_name && (
                            <Tag
                                color='green'
                                style={{
                                    minWidth: '75px',
                                    maxWidth: '250px',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                }}
                            >
                                {record?.origin_hub_name}
                            </Tag>
                        )}
                        {record?.origin_temple_name && (
                            <Tag
                                color='orange'
                                style={{
                                    minWidth: '75px',
                                    maxWidth: '250px',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'normal',
                                }}
                            >
                                {record?.origin_temple_name}
                            </Tag>
                        )}
                    </div>
                    <Tooltip placement="topLeft" title={store_id}>
                        <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}>
                            {store_id}
                        </div>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: <div> Title <br></br> {filterByTitleInput()}</div>,
            dataIndex: 'title',
            key: 'title',
            width: '15%',
            ellipsis: true,
            render: (title: string) => (
                <Tooltip placement="topLeft" title={title}>
                    {title}
                </Tooltip>
            ),
        },
        {
            title: 'Batch',
            dataIndex: 'batch_size',
            key: 'batch_size',
            width: '7%',
        },
        {
            title: 'Quantities ',
            dataIndex: 'quantities',
            key: 'quantities',
            width: '10%',
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: '8%',
            render: (text: number) => {
                if (text) {
                    return (
                        <Tag color="green" key={text} style={{ width: '100%', textAlign: 'center' }}>
                            {text.toString()}
                        </Tag>
                    );
                }
                return "-";
            },
        },
        {
            title: 'Position',
            dataIndex: 'position',
            key: 'position',
            width: '8%',
        },
        {
            title: 'Item type',
            dataIndex: 'item_type',
            key: 'item_type',
            width: '10%',
        },
        {
            title: 'Recommended',
            dataIndex: 'recommended',
            key: 'recommended',
            width: '12%',
            render: (text: boolean, record: any) => {
                if (text) {
                    return (
                        <Badge.Ribbon
                            text={text && 'Recommended'}
                            color="green"
                            style={{ marginTop: '-20px' }}
                        />
                    );
                } else {
                    return <>-</>;
                }
            },
        },
        {
            title: 'Campaign Ids',
            dataIndex: 'campaigns',
            key: 'campaigns',
            width: '20%',
            render: (text: string, record: any) => {
                if (record?.campaignList?.length) {
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '8px' }}>
                            {record?.campaignList?.map((campaign: string) => {
                                return (
                                    <Tag color="blue" key={campaign}>
                                        {campaign}
                                    </Tag>
                                );
                            })}
                        </div>
                    );
                } else {
                    return <p>-</p>;
                }
            },
        },
        {
            title: 'Experiments Linked',
            dataIndex: 'exp_linked',
            key: 'exp_linked',
            width: '14%',
            render: (text: boolean, record: any) => {
                return record?.exp_linked ? <Tag color="green"> {record?.exp_linked?.toString?.()}</Tag > : <Tag color='red'> {record?.exp_linked?.toString?.()}</Tag>
            },
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: '7%',
            render: (text: string, record: any) => {
                return (
                    <Badge.Ribbon
                        text={record.active ? 'Active' : 'InActive'}
                        color={record?.active ? 'green' : 'red'}
                        style={{ marginTop: '-20px' }}
                    />
                );
            },
        },
        {
            title: 'Edit',
            dataIndex: 'edit',
            key: 'edit',
            width: '6%',
            render: (text: string, record: any) => {
                return (
                    <Button
                        onClick={() => {
                            triggerEdit(record);
                        }}
                        type="link"
                        color="cyan"
                        style={{ cursor: 'pointer' }}
                    >
                        Edit
                    </Button>
                );
            },
            fixed: 'right',
        },
    ];


    // Fetch store list for chadhava drawer
    const fetchStoreList = useCallback(async (storeIdSearch: string = '', titleSearch: string = '') => {
        setStoreListLoading(true)
        try {
            const response = await monetisationOfferingsApi.storesApi.getStoreList(
                { limit: 20, offset: 0 },
                'manage_stores',
                countryState?.countryCode,
                '',
                searchParams.get("language_code") || "hi",
                'position',
                undefined,
                undefined,
                'offerings',
                storeIdSearch || undefined, // Pass store ID as query parameter
                titleSearch || undefined    // Pass title as query parameter
            )
            if (response?.data) {
                setStoreList(response.data.data?.store_list || [])
            }
        } catch (error) {
            console.error('Failed to fetch store list:', error)
            setStoreList([])
        }
        setStoreListLoading(false)
    }, [monetisationOfferingsApi.storesApi, countryState?.countryCode, searchParams])

    // Fetch hub list for mixed stores
    const fetchHubList = useCallback(async () => {
        try {
            const response = await monetisationOfferingsApi.hub.getHubList({
                limit: 1000,
                offset: 0,
            })
            if (response?.data) {
                setHubList(response.data.data.hubs as any || [])
            }
        } catch (error) {
            console.error('Failed to fetch hub list:', error)
            setHubList([])
        }
    }, [monetisationOfferingsApi.hub])

    // Fetch temple list for showing temple names
    const fetchTempleList = useCallback(async () => {
        try {
            const response = await monetisationOfferingsApi.templesApi.GetAllTemples()
            if (response?.data) {
                setTempleList(response.data.data)
            }
        } catch (error) {
            console.error('Failed to fetch temple list:', error)
            setTempleList([])
        }
    }, [monetisationOfferingsApi.templesApi])


    useEffect(() => {
        // Load hub list and temple list when Chadhava drawer opens
        if (showChadhavaDrawer && !selectedStore) {
            fetchHubList();
            fetchTempleList();
        }
    }, [showChadhavaDrawer, selectedStore, fetchHubList, fetchTempleList])

    // Debounced search effect to avoid too many API calls
    useEffect(() => {
        if (showChadhavaDrawer && !selectedStore) {
            const timeoutId = setTimeout(() => {
                fetchStoreList(drawerStoreIdSearch, drawerTitleSearch);
            }, 300); // 300ms debounce

            return () => clearTimeout(timeoutId);
        }
    }, [drawerStoreIdSearch, drawerTitleSearch, showChadhavaDrawer, selectedStore, fetchStoreList])


    // Fetch product list for a selected store
    const fetchProductList = async (storeId: string) => {
        setProductListLoading(true)
        try {
            const response = await monetisationOfferingsApi.storesApi.getProductList(storeId, countryState.countryCode, 'position')
            if (response?.data) {
                setProductListState(response?.data?.data?.products as any || [])
            }
        } catch (err) {
            console.error('Failed to fetch product list:', err)
            setProductListState([])
        }
        setProductListLoading(false)
    }


    // On Store row click: load products for that store
    const onStoreClick = (store: any) => {
        setSelectedStore(store)
        fetchProductList(store.id)
    }


    // Go back to store list from products view
    const backToStoreList = () => {
        setSelectedStore(null)
        setProductListState([])
    }


    const handleUpdateBtnClick = () => {
        setShowUpdateExpModal(true)
    }


    const closeModal = () => {
        setShowUpdateExpModal(false)
    }


    useEffect(() => {
        if (resetTrigger) {
            setCheckedAll(false)
            setSelectedRowKeys([])
            setResetTrigger(false)
        }
    }, [resetTrigger, setResetTrigger])


    useEffect(() => {
        setCheckedAll(false)
        setSelectedRowKeys([])
        setResetTrigger(false)
    }, [experiment, variant, setResetTrigger])


    const updateInventories = async () => {
        try {
            const updateResponse = await monetisationPujaApi.storesApi.updateInventories(
                store_id,
                countryState.countryCode,
                experiment as string,
                variant || "VARIANT",
                { inventory_ids: selectedRowKeys },
            )
            console.log(updateResponse)
            setShowUpdateExpModal(false)
            message.success("Inventories Updated Successfully")
            setCheckedAll(false)
            setSelectedRowKeys([])
            getStoreProductList()
        } catch (error) {
            console.error("Failed to update inventories:", error);
            setShowUpdateExpModal(false);
            showErrorToast(error)
            //message.error("Failed to update inventories")
            setCheckedAll(false)
            setSelectedRowKeys([])
            getStoreProductList()
        }
    }


    // Get hub name by origin_hub_id
    const getHubName = (originHubId: string) => {
        const hub = hubList.find((hub: any) => hub.id === originHubId)
        return hub ? hub.name : originHubId
    }

    // Get temple name by temple_id
    const getTempleName = (templeId: string) => {
        const temple = templeList?.temples_list?.find((temple: any) => temple.temple_id === templeId)
        return temple ? temple.name : templeId
    }

    // Return store list directly since filtering is now done by API
    const getFilteredStoreList = () => {
        return storeList
    }


    return (
        <div>
            <Row justify='space-between'>
                <Col style={{ paddingBottom: '10px' }}>
                    <Button onClick={() => setShowItemsPannel(!showItemsPanel)} type='dashed' size='small' style={{ borderColor: 'black' }}>
                        Show Items Panel
                    </Button>
                    <Button
                        onClick={() => setShowChadhavaDrawer(true)}
                        type='dashed'
                        size='small'
                        style={{ borderColor: 'black', marginLeft: '10px' }}
                    >
                        Show chadhava items Panel
                    </Button>
                </Col>
                <Col style={{ float: 'right' }}>
                    <SortAscendingOutlined /> Sort by : &nbsp;
                    <Select placeholder="Sort by price, position ..." value={sortBy} allowClear size='small' style={{ width: "200px" }} onChange={(value) => { updateSortBy(value) }}>
                        <Select.Option value="price"> Price</Select.Option>
                        <Select.Option value="position"> Position</Select.Option>
                        <Select.Option value="created_at"> Created at</Select.Option>
                    </Select>
                </Col>
            </Row>


            <Container style={{ width: '100%' }}>
                <Space direction={'vertical'} style={{ width: '100%' }}></Space>
                {
                    showItemsPanel &&
                    <ProductListDrawer
                        showItemsPanel={showItemsPanel}
                        setShowItemsPannel={setShowItemsPannel}
                        createItem={createItem}
                        added={added}
                        productList={productList}
                        searchQuery={searchQuery}
                        onSearchChange={onSearchChange}
                    />
                }


                <BodyContainer>
                    {filterData && <Table
                        dataSource={filterData}
                        columns={columns}
                        pagination={paginationParams}
                        onChange={handleTableChange}
                        loading={{
                            spinning: loading,
                            indicator: <Spin indicator={<CustomLoader />} />,
                        }}
                        scroll={{ x: 1600 }}
                    />}
                </BodyContainer>
            </Container>


            <Col span={24}>
                <LabelRightAlign>
                    <Space>
                        <Button type='primary' onClick={() => navigate("/monetisation/puja/temples")}>
                            Cancel
                        </Button>
                        {currentStep > 0 && (
                            <Button type='primary' onClick={previousHandler}>
                                Previous
                            </Button>
                        )}
                        {currentStep !== 2 && (
                            <Button type='primary' onClick={nextHandler}>
                                Next
                            </Button>
                        )}
                    </Space>
                </LabelRightAlign>
            </Col>


            {showUploadButton && (
                <Button
                    type="primary"
                    shape="circle"
                    icon={<UploadOutlined />}
                    size='large'
                    style={{
                        position: 'fixed',
                        bottom: 40,
                        right: 30,
                        zIndex: 100,
                        height: '50px',
                        width: '50px'
                    }}
                    onClick={handleUpdateBtnClick}
                />
            )}


            <Modal
                visible={showUpdateExpModal}
                closable={true}
                onCancel={closeModal}
                footer={[
                    <Button key='cancel' onClick={closeModal}>
                        Cancel
                    </Button>,
                    <Button key='done' type='primary' onClick={() => updateInventories()}>
                        Update
                    </Button>
                ]}
            >
                <div>
                    Are you sure you want to update the selected Inventories?
                </div>
            </Modal>


            {/* Chadhava Items Drawer */}
            <Drawer
                title={selectedStore ? (
                    <div>
                        <Button type="link" onClick={backToStoreList} style={{ padding: 0, marginRight: 8 }}>
                            ← Back
                        </Button>
                        {selectedStore.title}
                    </div>
                ) : "Chadhava Items - Store List"}
                placement="left"
                width={600}
                onClose={() => {
                    setShowChadhavaDrawer(false)
                    setSelectedStore(null)
                    setStoreListSearch('')
                    setDrawerStoreIdSearch('') // Clear drawer searches when closing
                    setDrawerTitleSearch('')
                }}
                visible={showChadhavaDrawer}
                destroyOnClose
            >
                {selectedStore ? (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Store ID:</Text> {selectedStore.id}<br />
                            <Text strong>Title:</Text> {selectedStore.title}
                        </div>
                        <Divider orientation="left">Items</Divider>


                        {productListLoading ? (
                            <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} indicator={<CustomLoader />} />
                        ) : (
                            <List
                                dataSource={productListState}
                                renderItem={(product: any) => {
                                    return product?.item_type === 'item' && (
                                        <ProductCard>
                                            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                {product?.origin_hub_id && (
                                                    <Tag color='blue'>{getHubName(product?.origin_hub_id)}</Tag>
                                                )}
                                                {product?.origin_temple_id && (
                                                    <Tag color='orange'>{getTempleName(product?.origin_temple_id)}</Tag>
                                                )}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: '16px',
                                                padding: '12px 0'
                                            }}>
                                                <img
                                                    src={product?.image_url}
                                                    alt="product"
                                                    style={{
                                                        width: '52px',
                                                        height: '52px',
                                                        borderRadius: '4px',
                                                        objectFit: 'cover'
                                                    }}
                                                />


                                                <div style={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '4px',
                                                    paddingLeft: '12px'
                                                }}>
                                                    <Text strong style={{ margin: 0 }}>{product.title}</Text>
                                                    <Text type="secondary" style={{ margin: 0 }}>ID: {product.id}</Text>
                                                </div>


                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '12px'
                                                }}>
                                                    <Input
                                                        value={product?.price}
                                                        disabled
                                                        style={{
                                                            width: '70px',
                                                            textAlign: 'center'
                                                        }}
                                                    />
                                                    <Button
                                                        type="primary"
                                                        onClick={() => added({
                                                            ...product,
                                                            store_id: selectedStore?.id,
                                                            origin_hub_name: getHubName(product?.origin_hub_id),
                                                            origin_temple_name: getTempleName(product?.origin_temple_id),
                                                            place_of_supply: product?.place_of_supply
                                                        }, 'chadhava')}
                                                    >
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </ProductCard>
                                    );
                                }}
                            />
                        )}
                    </div>
                ) : (
                    <>
                        {/* Search inputs inside the drawer */}
                        <Row style={{ marginBottom: '16px', display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Input
                                placeholder="Search by Store ID"
                                value={drawerStoreIdSearch}
                                onChange={(e) => setDrawerStoreIdSearch(e.target.value)}
                                allowClear
                                style={{ marginBottom: '8px', width: '48%' }}
                            />
                            <Input
                                placeholder="Search by Title"
                                value={drawerTitleSearch}
                                onChange={(e) => setDrawerTitleSearch(e.target.value)}
                                allowClear
                                style={{ marginBottom: '8px', width: '48%' }}
                            />
                        </Row>

                        {storeListLoading ? (
                            <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} indicator={<CustomLoader />} />
                        ) : (
                            <div>
                                {getFilteredStoreList().map(store => (
                                    <StoreCard
                                        key={store.id}
                                        onClick={() => onStoreClick(store)}
                                    ><Tag color='blue' style={{ marginBottom: "12px" }}>{store?.store_type}</Tag>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div>
                                                <Text strong>{store.title}</Text><br />
                                                <Text type="secondary">{store.id}</Text>
                                            </div>
                                            <Button type="link">View SKUs →</Button>
                                        </div>
                                    </StoreCard>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Drawer>
        </div>
    )
}


export default InventoryList
